import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";

import BreadCrumb from "../../../../../Components/Common/BreadCrumb";
import {
  closeSupportConversation,
  createSupportConversation,
  getSupportConversation,
  listSupportConversations,
  openSupportConversationTicket,
  postSupportConversationMessage,
  requestSupportHuman,
  respondSupportConversationSla,
  SupportConversation,
  SupportConversationMessage,
  SupportConversationTicketCategory,
} from "../../../../../services/supportConversationService";

const CHAT_CATEGORIES: SupportConversationTicketCategory[] = [
  "agenda",
  "orders",
  "payments",
  "account",
  "integration",
  "other",
];

const SUGGESTION_KEYS = [
  "supportChat.suggestions.pix",
  "supportChat.suggestions.schedule",
  "supportChat.suggestions.service",
] as const;

const formatMessageDateTime = (iso: string | null | undefined, locale: string): string => {
  if (!iso) {
    return "";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  return new Intl.DateTimeFormat(locale, {
    ...(sameDay
      ? { hour: "2-digit", minute: "2-digit" }
      : {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }),
  }).format(date);
};

const statusColor = (status: SupportConversation["status"]): string => {
  switch (status) {
    case "open_ai":
      return "primary";
    case "queued_human":
    case "assigned":
      return "warning";
    case "closed":
      return "secondary";
    default:
      return "light";
  }
};

const SupportChatPage = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [conversations, setConversations] = useState<SupportConversation[]>([]);
  const [active, setActive] = useState<SupportConversation | null>(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketMode, setTicketMode] = useState<"open" | "sla">("open");
  const [ticketCategory, setTicketCategory] =
    useState<SupportConversationTicketCategory>("other");
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const pollRef = useRef<number | null>(null);

  const openConversation = useMemo(
    () => conversations.find((c) => c.status !== "closed") ?? null,
    [conversations],
  );

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadList = useCallback(async () => {
    try {
      const response = await listSupportConversations({ per_page: 20 });
      if (response.success) {
        setConversations(response.data || []);
      }
    } catch {
      setError(t("supportChat.loadError"));
    }
  }, [t]);

  const loadConversation = useCallback(
    async (id: number, silent = false) => {
      if (!silent) {
        setLoading(true);
      }
      try {
        const response = await getSupportConversation(id);
        if (response.success && response.data) {
          setActive(response.data);
          setError(null);
        }
      } catch {
        if (!silent) {
          setError(t("supportChat.loadError"));
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [t],
  );

  useEffect(() => {
    void (async () => {
      setLoading(true);
      await loadList();
      setLoading(false);
    })();
  }, [loadList]);

  useEffect(() => {
    if (openConversation && (!active || active.id !== openConversation.id)) {
      void loadConversation(openConversation.id);
    }
  }, [openConversation, active, loadConversation]);

  useEffect(() => {
    if (!active || active.status === "closed") {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    pollRef.current = window.setInterval(() => {
      void loadConversation(active.id, true);
    }, 4000);

    return () => {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [active, loadConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [active?.messages?.length]);

  const handleStart = async () => {
    setSending(true);
    setError(null);
    try {
      const locale = i18n.language?.startsWith("en")
        ? "en"
        : i18n.language?.startsWith("es")
          ? "es"
          : "pt-BR";
      const response = await createSupportConversation(locale);
      if (response.success && response.data) {
        await loadList();
        setActive(response.data);
        toast.success(t("supportChat.started"));
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message || t("supportChat.startError");
      setError(message);
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    if (!active || active.status === "closed" || !draft.trim()) {
      return;
    }
    setSending(true);
    try {
      const response = await postSupportConversationMessage(active.id, draft.trim());
      if (response.success && response.data) {
        setDraft("");
        setActive(response.data.conversation);
        await loadList();
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message || t("supportChat.sendError");
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    if (!active) {
      return;
    }
    setSending(true);
    try {
      const response = await closeSupportConversation(active.id);
      if (response.success && response.data) {
        setActive(response.data);
        await loadList();
        toast.success(t("supportChat.closed"));
      }
    } catch {
      toast.error(t("supportChat.closeError"));
    } finally {
      setSending(false);
    }
  };

  const handleHuman = async () => {
    if (!active || active.status === "closed") {
      return;
    }
    setSending(true);
    try {
      const response = await requestSupportHuman(active.id);
      if (response.success && response.data) {
        setActive(response.data);
        toast.success(t("supportChat.humanRequested"));
      }
    } catch {
      toast.error(t("supportChat.humanError"));
    } finally {
      setSending(false);
    }
  };

  const openTicketModal = (mode: "open" | "sla") => {
    setTicketMode(mode);
    setTicketCategory("other");
    setTicketModalOpen(true);
  };

  const confirmTicketModal = async () => {
    if (!active || active.status === "closed") {
      return;
    }
    setSending(true);
    try {
      if (ticketMode === "open") {
        const response = await openSupportConversationTicket(
          active.id,
          ticketCategory
        );
        if (response.success && response.data) {
          setActive(response.data.conversation);
          await loadList();
          toast.success(
            t("supportChat.ticketOpened", { id: response.data.ticket.id })
          );
        }
      } else {
        const response = await respondSupportConversationSla(
          active.id,
          "accept_ticket",
          ticketCategory
        );
        if (response.success && response.data) {
          setActive(response.data);
          await loadList();
          toast.success(t("supportChat.ticketOpenedGeneric"));
        }
      }
      setTicketModalOpen(false);
    } catch {
      toast.error(t("supportChat.ticketError"));
    } finally {
      setSending(false);
    }
  };

  const handleSla = async (action: "accept_ticket" | "continue_waiting") => {
    if (!active) {
      return;
    }
    if (action === "accept_ticket") {
      openTicketModal("sla");
      return;
    }
    setSending(true);
    try {
      const response = await respondSupportConversationSla(active.id, action);
      if (response.success && response.data) {
        setActive(response.data);
        await loadList();
        toast.success(t("supportChat.slaContinue"));
      }
    } catch {
      toast.error(t("supportChat.ticketError"));
    } finally {
      setSending(false);
    }
  };

  const sendSuggestion = async (text: string) => {
    setSending(true);
    setError(null);
    try {
      let conversation = active;
      if (!conversation || conversation.status === "closed") {
        const locale = i18n.language?.startsWith("en")
          ? "en"
          : i18n.language?.startsWith("es")
            ? "es"
            : "pt-BR";
        const created = await createSupportConversation(locale);
        if (!created.success || !created.data) {
          throw new Error("create failed");
        }
        conversation = created.data;
        setActive(conversation);
        await loadList();
      }
      const response = await postSupportConversationMessage(
        conversation.id,
        text
      );
      if (response.success && response.data) {
        setActive(response.data.conversation);
        await loadList();
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message || t("supportChat.sendError");
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const suggestionChips = (
    <div className="d-flex flex-wrap gap-2 justify-content-center mt-3">
      {SUGGESTION_KEYS.map((key) => (
        <Button
          key={key}
          color="light"
          size="sm"
          className="border"
          disabled={sending}
          onClick={() => void sendSuggestion(t(key))}
        >
          {t(key)}
        </Button>
      ))}
    </div>
  );

  const messages: SupportConversationMessage[] = active?.messages || [];
  const slaMessage = [...messages]
    .reverse()
    .find((message) => message.meta?.kind === "sla_suggest");
  const lastMessage = messages[messages.length - 1];
  const awaitingAiReply =
    !!active &&
    active.status === "open_ai" &&
    lastMessage?.author_type === "user";
  const canType =
    !!active &&
    active.status !== "closed" &&
    (active.status === "open_ai" ||
      active.status === "queued_human" ||
      active.status === "assigned");

  useEffect(() => {
    if (awaitingAiReply) {
      scrollToBottom();
    }
  }, [awaitingAiReply]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("supportChat.title")}
          pageTitle={t("supportChat.breadcrumb")}
        />
        <ToastContainer />
        <Row>
          <Col xl={4} className="mb-3">
            <Card className="h-100">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">{t("supportChat.history")}</h5>
                  {!openConversation ? (
                    <Button color="primary" size="sm" onClick={handleStart} disabled={sending}>
                      {t("supportChat.new")}
                    </Button>
                  ) : null}
                </div>
                <Link to="/support/tickets" className="small d-inline-block mb-3">
                  {t("supportChat.ticketsLink")}
                </Link>
                {loading && !active ? (
                  <div className="text-center py-4">
                    <Spinner size="sm" />
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="text-muted">{t("supportChat.empty")}</div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {conversations.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={`btn btn-soft-${statusColor(item.status)} text-start`}
                        onClick={() => void loadConversation(item.id)}
                      >
                        <div className="d-flex justify-content-between">
                          <span>#{item.id}</span>
                          <Badge color={statusColor(item.status)}>
                            {t(`supportChat.status.${item.status}`)}
                          </Badge>
                        </div>
                        <small className="d-block text-muted">
                          {item.updated_at
                            ? new Date(item.updated_at).toLocaleString()
                            : ""}
                        </small>
                      </button>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
          <Col xl={8}>
            <Card>
              <CardBody>
                {!active ? (
                  <div className="text-center py-5">
                    <h4>{t("supportChat.emptyTitle")}</h4>
                    <p className="text-muted">{t("supportChat.emptyDescription")}</p>
                    <Button color="primary" onClick={handleStart} disabled={sending}>
                      {t("supportChat.new")}
                    </Button>
                    <p className="text-muted small mt-4 mb-0">
                      {t("supportChat.suggestionsHint")}
                    </p>
                    {suggestionChips}
                  </div>
                ) : (
                  <>
                    <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mb-3">
                      <div>
                        <h5 className="mb-1">
                          {t("supportChat.conversation")} #{active.id}
                        </h5>
                        <Badge color={statusColor(active.status)}>
                          {t(`supportChat.status.${active.status}`)}
                        </Badge>
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        {active.status === "open_ai" ? (
                          <Button
                            color="warning"
                            outline
                            size="sm"
                            onClick={handleHuman}
                            disabled={sending}
                          >
                            {t("supportChat.requestHuman")}
                          </Button>
                        ) : null}
                        {active.status !== "closed" && !active.support_ticket_id ? (
                          <Button
                            color="info"
                            outline
                            size="sm"
                            onClick={() => openTicketModal("open")}
                            disabled={sending}
                          >
                            {t("supportChat.openTicket")}
                          </Button>
                        ) : null}
                        {active.status !== "closed" ? (
                          <Button
                            color="secondary"
                            outline
                            size="sm"
                            onClick={handleClose}
                            disabled={sending}
                          >
                            {t("supportChat.close")}
                          </Button>
                        ) : null}
                      </div>
                    </div>

                    {active.support_ticket_id ? (
                      <div className="alert alert-info py-2">
                        {t("supportChat.ticketLinked", { id: active.support_ticket_id })}{" "}
                        <Link to={`/support/tickets/${active.support_ticket_id}`}>
                          {t("supportChat.ticketsLink")}
                        </Link>
                      </div>
                    ) : null}

                    {error ? <div className="alert alert-danger">{error}</div> : null}

                    <div
                      className="border rounded p-3 mb-3 bg-light"
                      style={{ minHeight: 320, maxHeight: 480, overflowY: "auto" }}
                    >
                      {messages.length === 0 ? (
                        <div className="text-center text-muted py-4">
                          <div>{t("supportChat.emptyThread")}</div>
                          <p className="small mt-3 mb-0">
                            {t("supportChat.suggestionsHint")}
                          </p>
                          {suggestionChips}
                        </div>
                      ) : null}
                      {messages.map((message) => (
                        <div
                          key={message.id}
                          className={`mb-3 ${
                            message.author_type === "user" ? "text-end" : "text-start"
                          }`}
                        >
                          <div
                            className={`d-inline-block text-start p-2 rounded ${
                              message.author_type === "user"
                                ? "bg-primary text-white"
                                : message.author_type === "system"
                                  ? "bg-white border text-muted"
                                  : "bg-white border"
                            }`}
                            style={{ maxWidth: "85%" }}
                          >
                            <div className="small fw-semibold mb-1">
                              {t(`supportChat.author.${message.author_type}`)}
                            </div>
                            <div style={{ whiteSpace: "pre-wrap" }}>{message.body}</div>
                            {message.help_links?.length ? (
                              <ul className="mb-0 mt-2 ps-3">
                                {message.help_links.map((link) => (
                                  <li key={link.url}>
                                    <a
                                      href={link.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={
                                        message.author_type === "user"
                                          ? "text-white"
                                          : undefined
                                      }
                                    >
                                      {link.title || link.url}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                            {message.created_at ? (
                              <div
                                className={`small mt-1 ${
                                  message.author_type === "user"
                                    ? "text-white-50"
                                    : "text-muted"
                                }`}
                              >
                                {formatMessageDateTime(message.created_at, i18n.language)}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ))}
                      {awaitingAiReply ? (
                        <div className="text-start mb-2">
                          <div className="d-inline-flex align-items-center gap-2 p-2 rounded bg-white border text-muted">
                            <Spinner size="sm" />
                            <span className="small">{t("supportChat.thinking")}</span>
                          </div>
                        </div>
                      ) : null}
                      <div ref={bottomRef} />
                    </div>

                    {slaMessage && active.status === "queued_human" ? (
                      <div className="alert alert-warning mb-3 sticky-bottom">
                        <div className="fw-semibold mb-2">
                          {t("supportChat.slaBanner")}
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                          <Button
                            color="primary"
                            size="sm"
                            disabled={sending}
                            onClick={() => void handleSla("accept_ticket")}
                          >
                            {t("supportChat.slaAccept")}
                          </Button>
                          <Button
                            color="secondary"
                            outline
                            size="sm"
                            disabled={sending}
                            onClick={() => void handleSla("continue_waiting")}
                          >
                            {t("supportChat.slaContinueBtn")}
                          </Button>
                        </div>
                      </div>
                    ) : null}

                    {canType ? (
                      <div className="d-flex gap-2">
                        <Input
                          type="textarea"
                          rows={2}
                          value={draft}
                          disabled={sending}
                          placeholder={t("supportChat.placeholder")}
                          onChange={(e) => setDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              void handleSend();
                            }
                          }}
                        />
                        <Button
                          color="primary"
                          onClick={handleSend}
                          disabled={sending || !draft.trim()}
                        >
                          {sending ? <Spinner size="sm" /> : t("supportChat.send")}
                        </Button>
                      </div>
                    ) : (
                      <div className="alert alert-secondary mb-0">
                        {t("supportChat.closedHint")}
                        <Button
                          color="link"
                          className="p-0 ms-2 align-baseline"
                          onClick={handleStart}
                          disabled={sending || !!openConversation}
                        >
                          {t("supportChat.new")}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal
          isOpen={ticketModalOpen}
          toggle={() => !sending && setTicketModalOpen(false)}
          centered
        >
          <ModalHeader toggle={() => !sending && setTicketModalOpen(false)}>
            {t("supportChat.categoryModalTitle")}
          </ModalHeader>
          <ModalBody>
            <p className="text-muted small">{t("supportChat.categoryModalHint")}</p>
            <Label for="support-chat-category">
              {t("supportChat.categoryLabel")}
            </Label>
            <Input
              id="support-chat-category"
              type="select"
              value={ticketCategory}
              onChange={(e) =>
                setTicketCategory(
                  e.target.value as SupportConversationTicketCategory
                )
              }
            >
              {CHAT_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {t(`support.categories.${category}`)}
                </option>
              ))}
            </Input>
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              disabled={sending}
              onClick={() => setTicketModalOpen(false)}
            >
              {t("supportChat.categoryCancel")}
            </Button>
            <Button color="primary" disabled={sending} onClick={() => void confirmTicketModal()}>
              {sending ? <Spinner size="sm" /> : t("supportChat.openTicket")}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default SupportChatPage;
